import { JSONSchema7 } from 'json-schema';
import { customSchemas, types as commonTypes } from '@sitebuilder/common';
import { annotationSchema, annotationsFilter, annotationsFilterUiSchema } from '../Annotations';
import FundList from './FundList';

const schema: JSONSchema7 = {
    title: 'Fund List',
    type: 'object',
    required: [],
    properties: {
        styles: {
            type: 'string',
            description: 'A stylesheet',
            default: '',
        },
        ...customSchemas.textSchema.TextSchema.schema,
        ...customSchemas.styleSchema.StyleSchema.schema,
        ...customSchemas.layoutSchema.LayoutSchema.schema,
        iconShowContent: {
            type: 'string',
            title: 'Show Content Icon Name',
            default: 'fas fa-plus-circle',
        },
        iconHideContent: {
            type: 'string',
            title: 'Hide Content Icon Name',
            default: 'fas fa-minus-circle',
        },
        useIconForFavoriteColumn: {
            type: 'string',
            title: 'Use icon for favorite column',
            description: 'Enter icon name. If empty, default icon name appears',
        },
        addPlaceholderAfterFilters: {
            type: 'boolean',
            title: 'Add Placeholder After Filters',
            default: false,
        },
        isShowEmailButton: {
            type: 'boolean',
            title: 'Is show email button',
            default: false,
            dependencies: {},
        },
        isShowCopyButton: {
            type: 'boolean',
            title: 'Is show copy button',
            default: false,
            dependencies: {},
        },
        isShowClearSelectionButton: {
            type: 'boolean',
            title: 'Show Clear Selection Button',
            default: false,
        },
        firstColumnOptions: {
            type: 'object',
            title: 'First Column Options',
            properties: {
                type: {
                    type: 'string',
                    enum: ['checkList', 'favorites', 'hide column'],
                    default: 'favorites',
                    title: 'Column Type',
                },
            },
            dependencies: {
                type: {
                    oneOf: [
                        {
                            properties: {
                                type: { enum: ['favorites'] },
                                storageName: {
                                    type: 'string',
                                    description: 'Local storage item name',
                                    title: 'Storage Name',
                                    default: 'favorites',
                                },
                                id: {
                                    type: 'string',
                                    title: 'Enter property Id',
                                },
                                jsonPath: {
                                    type: 'string',
                                    title: 'Enter json path in model',
                                },
                                iconType: {
                                    type: 'string',
                                    enum: ['Font-awesome', 'Icon-URL'],
                                    title: 'Column Icon Type',
                                    default: 'Font-awesome',
                                },
                            },
                            dependencies: {
                                iconType: {
                                    oneOf: [
                                        {
                                            properties: {
                                                iconType: { enum: ['Font-awesome'] },
                                                addIconName: {
                                                    type: 'string',
                                                    title: 'Add favorite Icon Name',
                                                    default: 'far fa-star',
                                                },
                                                removeIconName: {
                                                    type: 'string',
                                                    title: 'Remove favorite Icon Name',
                                                    default: 'fas fa-star',
                                                },
                                            },
                                            required: ['addIconName', 'removeIconName'],
                                        },
                                        {
                                            properties: {
                                                iconType: { enum: ['Icon-URL'] },
                                                addIconURL: {
                                                    type: 'string',
                                                    title: 'Add favorite Icon URL',
                                                    default: '',
                                                },
                                                removeIconURL: {
                                                    type: 'string',
                                                    title: 'Remove favorite Icon URL',
                                                    default: '',
                                                },
                                            },
                                            required: ['addIconURL', 'removeIconURL'],
                                        },
                                    ],
                                },
                            },
                        },
                        {
                            properties: {
                                type: { enum: ['checkList'] },
                            },
                        },
                        {
                            properties: {
                                type: { enum: ['hide column'] },
                            },
                        },
                    ],
                },
            },
        },
        selectedListStorageName: {
            type: 'string',
            description: 'Local storage item name',
            title: 'Storage Name',
            default: 'selectedlist',
        },
        selectedListDataId: {
            type: 'string',
            title: 'Enter property Id',
        },
        maxSelectionList: {
            type: 'number',
            title: 'Max Selection List',
            default: 10,
        },
        selectedListJsonPath: {
            type: 'string',
            title: 'Enter json path in model',
        },
        hideTabHeader: {
            type: 'boolean',
            enum: [true, false],
            title: 'Hide Tab Header',
            default: false,
        },
        views: {
            type: 'array',
            title: 'Add Views',
            items: {
                type: 'object',
                required: ['id', 'label'],
                properties: {
                    id: {
                        type: 'string',
                        title: 'Enter view id',
                    },
                    label: {
                        type: 'string',
                        title: 'Enter view label',
                    },
                    subHeading: {
                        type: 'string',
                        title: 'Enter view subheading',
                    },
                    columns: {
                        type: 'array',
                        title: 'Add Columns',
                        items: {
                            type: 'object',
                            required: ['id', 'label', 'jsonPath'],
                            properties: {
                                id: {
                                    type: 'string',
                                    title: 'Enter property Id',
                                },
                                label: {
                                    type: 'string',
                                    title: 'Enter column label',
                                },
                                jsonPath: {
                                    type: 'string',
                                    title: 'Enter json path in model',
                                },
                                format: {
                                    type: 'string',
                                    title: 'Enter format',
                                },
                                nullGuard: {
                                    type: 'string',
                                    title: 'Enter null gaurd',
                                },
                                noDataAnnotationId: {
                                    type: 'string',
                                    // eslint-disable-next-line max-len
                                    title: 'Enter markerTargetId to show annotation when there is no data',
                                },
                                annotationId: {
                                    type: 'string',
                                    // eslint-disable-next-line max-len
                                    title: 'Enter markerTargetId to show annotation when there is data',
                                },
                                additionalId: {
                                    type: 'string',
                                    title: 'Enter additional property Id (for twoRow cell types)',
                                },
                                additionalJsonPath: {
                                    type: 'string',
                                    // eslint-disable-next-line
                                    title: 'Enter additional json path in model (for twoRow cell types)',
                                },
                                additionalFormat: {
                                    type: 'string',
                                    title: 'Enter additional format',
                                },
                                disableSorting: {
                                    type: 'boolean',
                                    enum: [true, false],
                                    title: 'Disable Sorting',
                                    default: false,
                                },
                                transformFunction: {
                                    type: 'string',
                                    title: 'Add transform function for column',
                                    default: 'return (data) => { return data }',
                                },
                                columnGrouping: {
                                    type: 'string',
                                    title: 'Id of column group',
                                },
                                columnType: {
                                    type: 'string',
                                    // eslint-disable-next-line max-len
                                    title: 'Can be risk, rating, price, documents, twoRow, checklist',
                                },
                                hideColumn: {
                                    type: 'boolean',
                                    title: 'Hide column from view',
                                    default: false,
                                },
                                excelOptions: {
                                    type: 'object',
                                    title: 'Column options for download file',
                                    properties: {
                                        showColumn: {
                                            type: 'boolean',
                                            title: 'Show column in download file',
                                        },
                                        excelLabel: {
                                            type: 'string',
                                            title: 'Enter excel file label',
                                        },
                                        dataKey: {
                                            type: 'string',
                                            title: 'Enter data key for excel column',
                                        },
                                        format: {
                                            type: 'string',
                                            title: 'Enter format for excel column',
                                        },
                                        columnType: {
                                            type: 'string',
                                            // eslint-disable-next-line max-len
                                            title: 'Can be risk, rating, price, documents, twoRow, checklist',
                                        },
                                    },
                                },
                            },
                        },
                    },
                    columnGroups: {
                        type: 'array',
                        title: 'Add Columns groups',
                        items: {
                            type: 'object',
                            required: ['id', 'label'],
                            properties: {
                                id: {
                                    type: 'string',
                                    title: 'Enter property Id',
                                },
                                label: {
                                    type: 'string',
                                    title: 'Enter column label',
                                },
                            },
                        },
                    },
                },
            },
        },
        hideAllFilters: {
            type: 'boolean',
            enum: [true, false],
            title: 'Hide Filters',
            default: false,
        },
        searchLabel: {
            type: 'string',
            title: 'Enter Search label',
        },
        searchTitle: {
            type: 'string',
            title: 'Enter Search title',
        },
        filters: {
            type: 'array',
            title: 'Add Filters',
            items: {
                type: 'object',
                required: ['id', 'label', 'jsonPath', 'matchesFrom'],
                properties: {
                    id: {
                        type: 'string',
                        title: 'Enter filter id',
                    },
                    label: {
                        type: 'string',
                        title: 'Enter filter label',
                    },
                    jsonPath: {
                        type: 'string',
                        title: 'Enter json path in model',
                    },
                    matchesFrom: {
                        type: 'string',
                        title: 'Enter value or array',
                    },
                    defaultValue: {
                        type: 'string',
                        title: 'Enter default value',
                    },
                    sortOrderId: {
                        type: 'string',
                        title: 'Enter sort order id',
                    },
                    sortOrderJsonPath: {
                        type: 'string',
                        title: 'Enter sort json path',
                    },
                },
            },
        },
        grouping: {
            title: 'Enable Grouping',
            type: 'object',
            properties: {
                enabled: {
                    type: 'boolean',
                    enum: [true, false],
                    title: 'Enable grouping',
                    default: false,
                },
                propertyId: {
                    type: 'string',
                    title: 'Enter filter id',
                },
                jsonPath: {
                    type: 'string',
                    title: 'Enter json path in model',
                },
            },
        },
        fundUrl: {
            type: 'string',
            title: 'Fund URL',
            description: 'Enter path property from data',
        },
        fundUrlBase: {
            type: 'string',
            title: 'Fund base URL',
            description:
                'fund url base- this property will be ignored if you provide Fund URL above',
            default: '',
        },
        fundPdfUrlBase: {
            type: 'string',
            title: 'Fund profile link',
            description: 'fund profile PDF url link',
            default: '',
        },
        fundPdfName: {
            type: 'string',
            title: 'Fund profile name',
            description: 'fund profile PDF name',
            default: '',
        },
        fundUrlMapping: {
            type: 'array',
            title: 'Map to correct fund urls',
            items: {
                type: 'object',
                properties: {
                    parameters: {
                        type: 'array',
                        title: 'Parameters to match',
                        items: {
                            type: 'object',
                            required: ['key', 'value'],
                            properties: {
                                key: {
                                    type: 'string',
                                    title: 'Enter parameter key',
                                },
                                value: {
                                    type: 'string',
                                    title: 'Enter parameter value',
                                },
                            },
                        },
                    },
                    fundUrlTag: {
                        type: 'string',
                        title: 'Enter fund url tag from documents array',
                    },
                },
            },
        },
        constantDataPoints: {
            type: 'array',
            title: 'Add constant data points to model',
            items: {
                type: 'object',
                properties: {
                    key: {
                        type: 'string',
                        title: 'Enter data key',
                    },
                    value: {
                        type: 'string',
                        title: 'Enter data value',
                    },
                },
            },
        },
        downloadFileOptions: {
            type: 'object',
            title: 'Download File Options',
            properties: {
                fileType: {
                    type: 'string',
                    enum: ['.xlsx', '.csv'],
                    title: 'Download File Type',
                },
            },
            dependencies: {
                fileType: {
                    oneOf: [
                        {
                            properties: {
                                fileType: { enum: ['.xlsx'] },
                                excelTabs: {
                                    type: 'array',
                                    title: 'Excel sheet tabs option',
                                    items: {
                                        type: 'object',
                                        properties: {
                                            name: {
                                                type: 'string',
                                                title: 'Sheet tab name',
                                            },
                                            showDisclaimer: {
                                                type: 'boolean',
                                                title: 'Show disclaimer',
                                            },
                                            disclaimers: {
                                                type: 'array',
                                                title: 'Disaclimer option',
                                                items: {
                                                    type: 'object',
                                                    properties: {
                                                        filterBy: annotationSchema,
                                                        filters: {
                                                            ...annotationsFilter,
                                                            title: 'Filter By',
                                                        },
                                                        showMarker: {
                                                            type: 'boolean',
                                                            title: 'Show Marker',
                                                            default: false,
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    },
                                },
                            },
                        },
                        {
                            properties: {
                                fileType: { enum: ['.csv'] },
                                csvOptions: {
                                    type: 'object',
                                    properties: {
                                        showDisclaimer: {
                                            type: 'boolean',
                                            title: 'Show disclaimer',
                                        },
                                        disclaimers: {
                                            type: 'array',
                                            title: 'Disaclimer option',
                                            items: {
                                                type: 'object',
                                                properties: {
                                                    filterBy: annotationSchema,
                                                    filters: {
                                                        ...annotationsFilter,
                                                        title: 'Filter By',
                                                    },
                                                    showMarker: {
                                                        type: 'boolean',
                                                        title: 'Show Marker',
                                                        default: false,
                                                    },
                                                },
                                            },
                                        },
                                    },
                                },
                            },
                        },
                    ],
                },
            },
        },
        transformFunction: {
            type: 'string',
            title: 'Add transform function',
            default: 'return (data) => { return data }',
        },
    },
    dependencies: {
        ...customSchemas.layoutSchema.LayoutSchema.dependencies,
        hideAllFilters: {
            oneOf: [
                {
                    properties: {
                        hideAllFilters: { enum: [false] },
                        filters: {
                            type: 'array',
                            title: 'Add Filters',
                            items: {
                                type: 'object',
                                required: ['id', 'label', 'jsonPath', 'matchesFrom'],
                                properties: {
                                    id: {
                                        type: 'string',
                                        title: 'Enter filter id',
                                    },
                                    label: {
                                        type: 'string',
                                        title: 'Enter filter label',
                                    },
                                    jsonPath: {
                                        type: 'string',
                                        title: 'Enter json path in model',
                                    },
                                    matchesFrom: {
                                        type: 'string',
                                        title: 'Enter value or array',
                                    },
                                    defaultValue: {
                                        type: 'string',
                                        title: 'Enter default value',
                                    },
                                    sortOrderId: {
                                        type: 'string',
                                        title: 'Enter sort order id',
                                    },
                                    sortOrderJsonPath: {
                                        type: 'string',
                                        title: 'Enter sort json path',
                                    },
                                },
                            },
                        },
                    },
                },
            ],
        },
        isShowEmailButton: {
            oneOf: [
                {
                    properties: {
                        isShowEmailButton: { enum: [true] },
                        emailBtnLocalStorageItemName: {
                            type: 'string',
                            description: 'Local storage item name',
                            title: 'Storage Name',
                        },
                        emailBtnDataKey: {
                            type: 'string',
                            title: 'Data key',
                        },
                        receiverEmailId: {
                            type: 'string',
                            title: 'Receiver Email Id',
                            default: '',
                        },
                        emailSubject: {
                            type: 'string',
                            title: 'Email Subject',
                            default: '',
                        },
                        emailOnHoverText: {
                            type: 'string',
                            title: 'Hover text',
                            default: '',
                        },
                        emailButtonText: {
                            type: 'string',
                            title: 'Button text',
                            default: '',
                        },
                        emailBtnTransformFunc: {
                            type: 'string',
                            title: 'Transform function',
                            default: 'return (data) => { return `This is test email` }',
                        },
                    },
                },
            ],
        },
        isShowCopyButton: {
            oneOf: [
                {
                    properties: {
                        isShowCopyButton: { enum: [true] },
                        copyBtnLocalStorageItemName: {
                            type: 'string',
                            description: 'Local storage item name',
                            title: 'Storage Name',
                        },
                        copyBtnDataKey: {
                            type: 'string',
                            title: 'Data key',
                        },
                        copyOnHoverText: {
                            type: 'string',
                            title: 'Hover text',
                            default: '',
                        },
                        copyButtonText: {
                            type: 'string',
                            title: 'Button text',
                            default: '',
                        },
                        copyBtnTransformFunc: {
                            type: 'string',
                            title: 'Transform function',
                            default: 'return (data) => { return `Copied text!` }',
                        },
                    },
                },
            ],
        },
        isShowClearSelectionButton: {
            oneOf: [
                {
                    properties: {
                        isShowClearSelectionButton: { enum: [true] },
                        clearSelectionButtonName: {
                            type: 'string',
                            title: 'Clear Selection Button Name',
                            default: '',
                        },
                    },
                },
                {
                    properties: {
                        isShowClearSelectionButton: { enum: [false] },
                    },
                },
            ],
        },
    },
};

const groups = [
    {
        'Style Editor': ['styles'],
        ...customSchemas.textSchema.TextSchema.group,
        ...customSchemas.styleSchema.StyleSchema.group,
        ...customSchemas.layoutSchema.LayoutSchema.group,
        Values: [
            'iconShowContent',
            'iconHideContent',
            'isShowClearSelectionButton',
            'clearSelectionButtonName',
            'useIconForFavoriteColumn',
            'addPlaceholderAfterFilters',
            'firstColumnOptions',
            'hideTabHeader',
            'views',
            'hideAllFilters',
            'searchLabel',
            'searchTitle',
            'filters',
            'grouping',
            'fundUrl',
            'fundUrlBase',
            'fundPdfUrlBase',
            'fundPdfName',
            'fundUrlMapping',
            'constantDataPoints',
            'downloadFileOptions',
        ],
        'Transform Data': ['transformData', 'transformFunction'],
        'Email Button': [
            'isShowEmailButton',
            'emailButtonText',
            'emailOnHoverText',
            'receiverEmailId',
            'emailSubject',
            'emailBtnLocalStorageItemName',
            'emailBtnDataKey',
            'emailBtnTransformFunc',
        ],
        'Copy Button': [
            'isShowCopyButton',
            'copyButtonText',
            'copyOnHoverText',
            'copyBtnLocalStorageItemName',
            'copyBtnDataKey',
            'copyBtnTransformFunc',
        ],
        'Selected List': [
            'selectedListStorageName',
            'selectedListDataId',
            'maxSelectionList',
            'selectedListJsonPath',
        ],
    },
];

const FundListWidget: commonTypes.IWidget = {
    schema,
    uiSchema: {
        'ui:groups': groups,
        ...customSchemas.textSchema.TextSchema.uiSchema,
        ...customSchemas.styleSchema.StyleSchema.uiSchema,
        ...customSchemas.layoutSchema.LayoutSchema.uiSchema,
        styles: {
            'ui:widget': 'CSSEditor',
        },
        hideTabHeader: {
            'ui:widget': 'CustomCheckbox',
        },
        views: {
            items: {
                id: { 'ui:widget': 'ResourcePicker' },
                label: { 'ui:widget': 'ResourcePicker' },
                subHeading: { 'ui:widget': 'ResourcePicker' },
                columns: {
                    items: {
                        'ui:field': 'CollapsibleFieldTemplate',
                        id: { 'ui:widget': 'ResourcePicker' },
                        label: { 'ui:widget': 'ResourcePicker' },
                        jsonPath: { 'ui:widget': 'ResourcePicker' },
                        format: { 'ui:widget': 'ResourcePicker' },
                        nullGuard: { 'ui:widget': 'ResourcePicker' },
                        noDataAnnotationId: { 'ui:widget': 'ResourcePicker' },
                        annotationId: { 'ui:widget': 'ResourcePicker' },
                        additionalId: { 'ui:widget': 'ResourcePicker' },
                        additionalJsonPath: { 'ui:widget': 'ResourcePicker' },
                        additionalFormat: { 'ui:widget': 'ResourcePicker' },
                        disableSorting: {
                            'ui:widget': 'CustomCheckbox',
                        },
                        transformFunction: {
                            'ui:widget': 'JSEditor',
                        },
                        columnGrouping: { 'ui:widget': 'ResourcePicker' },
                        columnType: { 'ui:widget': 'ResourcePicker' },
                        hideColumn: {
                            'ui:widget': 'CustomCheckbox',
                        },
                        excelOptions: {
                            showColumn: {
                                'ui:widget': 'CustomCheckbox',
                            },
                            excelLabel: { 'ui:widget': 'ResourcePicker' },
                            dataKey: { 'ui:widget': 'ResourcePicker' },
                            format: { 'ui:widget': 'ResourcePicker' },
                            columnType: { 'ui:widget': 'ResourcePicker' },
                        },
                    },
                },
                columnGroups: {
                    items: {
                        properties: {
                            id: { 'ui:widget': 'ResourcePicker' },
                            label: { 'ui:widget': 'ResourcePicker' },
                        },
                    },
                },
            },
        },
        hideAllFilters: {
            'ui:widget': 'CustomCheckbox',
        },
        grouping: {
            enabled: {
                'ui:widget': 'CustomCheckbox',
            },
            propertyId: { 'ui:widget': 'ResourcePicker' },
            jsonPath: { 'ui:widget': 'ResourcePicker' },
        },
        searchLabel: { 'ui:widget': 'ResourcePicker', 'ui:placeholder': '{{ }}' },
        searchTitle: { 'ui:widget': 'ResourcePicker', 'ui:placeholder': '{{ }}' },
        fundUrl: { 'ui:widget': 'ResourcePicker', 'ui:placeholder': '{{ }}' },
        fundUrlBase: { 'ui:widget': 'ResourcePicker' },
        fundPdfUrlBase: { 'ui:widget': 'ResourcePicker' },
        fundPdfName: { 'ui:widget': 'ResourcePicker' },
        fundUrlMapping: {
            items: {
                parameters: {
                    items: {
                        key: { 'ui:widget': 'ResourcePicker' },
                        value: { 'ui:widget': 'ResourcePicker' },
                    },
                },
                fundUrlTag: { 'ui:widget': 'ResourcePicker' },
            },
        },
        transformFunction: {
            'ui:widget': 'JSEditor',
        },
        downloadFileOptions: {
            fileType: { 'ui:widget': 'CustomSelect' },
            excelTabs: {
                items: {
                    disclaimers: {
                        items: {
                            ...annotationsFilterUiSchema,
                            showMarker: {
                                'ui:widget': 'CustomCheckbox',
                            },

                            filterBy: {
                                'ui:widget': 'ResourcePicker',
                            },
                        },
                    },
                    showDisclaimer: {
                        'ui:widget': 'CustomCheckbox',
                    },
                    name: { 'ui:widget': 'ResourcePicker' },
                },
            },
            csvOptions: {
                showDisclaimer: {
                    'ui:widget': 'CustomCheckbox',
                },
                disclaimers: {
                    items: {
                        ...annotationsFilterUiSchema,
                        showMarker: {
                            'ui:widget': 'CustomCheckbox',
                        },
                        filterBy: {
                            'ui:widget': 'ResourcePicker',
                        },
                    },
                },
            },
        },
        addPlaceholderAfterFilters: {
            'ui:widget': 'CustomCheckbox',
        },
        isShowEmailButton: {
            'ui:widget': 'CustomCheckbox',
        },
        receiverEmailId: { 'ui:widget': 'ResourcePicker' },
        emailSubject: { 'ui:widget': 'ResourcePicker' },
        emailOnHoverText: { 'ui:widget': 'ResourcePicker' },
        emailButtonText: { 'ui:widget': 'ResourcePicker' },
        emailBtnTransformFunc: { 'ui:widget': 'JSEditor' },
        emailBtnDataKey: { 'ui:widget': 'ResourcePicker' },
        emailBtnLocalStorageItemName: { 'ui:widget': 'ResourcePicker' },
        isShowCopyButton: {
            'ui:widget': 'CustomCheckbox',
        },
        copyOnHoverText: { 'ui:widget': 'ResourcePicker' },
        copyButtonText: { 'ui:widget': 'ResourcePicker' },
        copyBtnTransformFunc: { 'ui:widget': 'JSEditor' },
        copyBtnDataKey: { 'ui:widget': 'ResourcePicker' },
        copyBtnLocalStorageItemName: { 'ui:widget': 'ResourcePicker' },
        selectedListStorageName: { 'ui:widget': 'ResourcePicker' },
        selectedListDataId: { 'ui:widget': 'ResourcePicker' },
        maxSelectionList: { 'ui:widget': 'ResourcePicker' },
        selectedListJsonPath: { 'ui:widget': 'ResourcePicker' },
        isShowClearSelectionButton: {
            'ui:widget': 'CustomCheckbox',
        },
        clearSelectionButtonName: { 'ui:widget': 'ResourcePicker' },
        iconShowContent: { 'ui:widget': 'ResourcePicker' },
        iconHideContent: { 'ui:widget': 'ResourcePicker' },
        useIconForFavoriteColumn: { 'ui:widget': 'ResourcePicker' },
        firstColumnOptions: {
            type: { 'ui:widget': 'CustomSelect' },
            storageName: { 'ui:widget': 'ResourcePicker' },
            id: { 'ui:widget': 'ResourcePicker' },
            jsonPath: { 'ui:widget': 'ResourcePicker' },
            iconType: { 'ui:widget': 'CustomSelect' },
            addIconName: { 'ui:widget': 'ResourcePicker' },
            removeIconName: { 'ui:widget': 'ResourcePicker' },
            addIconURL: { 'ui:widget': 'ResourcePicker' },
            removeIconURL: { 'ui:widget': 'ResourcePicker' },
        },
    },
    templateView: FundList,
    id: 'FundList',
    group: 'component',
    contentPlaceHolders: ['children'],
    initialData: () => ({}),
};

export default FundListWidget;
